import type { PoiCategory } from "@situm/sdk-js";

import type { NavigationStatus, Poi, Position } from "@/libs/models";
import type { RouteType } from "../directions/types";
import type { SDKPoi } from "../utils/sdkMapper";

export interface NavigateToPoi {
  navigationTo: Poi["id"];
  type?: RouteType;
}

export interface NavigateToFilter {
  filter: {
    poiCategoryIdentifier?: PoiCategory["id"];
    // ... other future filters
  };
  type?: RouteType;
}

export interface PendingNavigationRequest {
  payload: NavigateToPoi | NavigateToFilter;
}

export type OnNavigationStartRequestedPayload =
  | NavigateToPoi
  | NavigateToFilter;

export interface NavigateToPoint {
  lat: number;
  lng: number;
  floorIdentifier: string;
  type?: RouteType;
  navigationName?: string;
}

export interface SliceProps {
  navigationStatus: NavigationStatus;
  navigationFrom: Poi["id"] | null;
  navigationTo: Poi["id"] | null;
  pendingNavigationRequest: PendingNavigationRequest | null;
  destinationPosition: Position | null;
  lastNavigationTo: Poi["id"] | null;
  nearestOriginNode: any;
  nearestDestinationNode: any;
  route: Route | null;
  tags: RouteType[];
  errors: Array<NAVIGATION_ERROR_CODES>;
  navigationName?: string;
  speakAloudIndications: boolean;
}

// started, destination_reached and OOR share common payload:
//    viewer.navigation.started's payload -> NativeNavigationStatusPayload
//    viewer.navigation.updated's payload w/ type=='OUT_OF_ROUTE' -> NativeNavigationStatusPayload & type
//    viewer.navigation.updated's payload w/ type=='DESTINATION_REACHED' -> NativeNavigationStatusPayload & type
// viewer.navigation.updated's payload w/ type=='PROGRESS' -> NativeNavigationProgressPayload & type

export interface NativeNavigationStatusPayload {
  buildingIdentifier: number;
  indications: RouteIndication[];
  points: RouteNode[];
  steps: RouteStep[];
  segments: RouteSegment[];
  poiTo: SDKPoi;
  accessibilityMode: RouteType;
}

// used just in Updated when type is PROGRESS
export interface NativeNavigationProgressPayload {
  distanceToEndStep: number;
  distanceToClosestPointInRoute: number;
  timeToEndStep: number;
  timeToGoal: number;
  currentStepIndex: number;
  distanceToGoal: number;

  currentIndication: RouteIndication;
  nextIndication: RouteIndication;
  routeStep: RouteStep;

  points: RouteNode[];
  segments: RouteSegment[];
}

export type NativeNavigationUpdatedPayloadType<
  T extends "STATUS" | "PROGRESS",
> = T extends "STATUS"
  ? NativeNavigationStatusPayload
  : T extends "PROGRESS"
    ? NativeNavigationProgressPayload
    : never;

export type NativeNavigationUpdatedPayload<T extends "STATUS" | "PROGRESS"> =
  NativeNavigationUpdatedPayloadType<T> & { type: string };

export enum NativeNavigationUpdateTypes {
  STARTED = "STARTED",
  PROGRESS = "PROGRESS",
  DESTINATION_REACHED = "DESTINATION_REACHED",
  OOR = "OUT_OF_ROUTE",
  CANCELLED = "CANCELLED",
}

export enum NAVIGATION_ERROR_CODES {
  untraceable_route,
  no_location,
  outdoor_oob_location,
}

export interface Route {
  identifier: number;
  TO: RouteNode | null;
  from: RouteNode | null;
  indications: RouteIndication[];
  steps: RouteStep[];
  nodes: RouteNode[];
  lastStep: RouteStep | null;
  points: RouteNode[];
  segments: RouteSegment[];
  firstStep: RouteStep | null;
  edges: RouteStep[];
  currentStep: RouteStep | null;
  currentIndication: RouteIndication | null;
  nextIndication: RouteIndication | null;
}

export interface RouteNode {
  isOutdoor: boolean;
  coordinate: Coordinate;
  isIndoor: boolean;
  cartesianCoordinate: CartesianCoordinate;
  floorIdentifier: string;
  buildingIdentifier: string;
}

export interface CartesianCoordinate {
  y: number;
  x: number;
}

export interface Coordinate {
  longitude: number;
  latitude: number;
}

export interface RouteStep {
  isLast: boolean;
  to: RouteNode;
  from: RouteNode;
  isFirst: boolean;
  id: number;
  distanceToGoal: number;
  distance: number;
  orientation: number;
}

export interface RouteIndication {
  humanReadableMessage: string;
  stepIdxOrigin: number;
  distance: number;
  orientation: number;
  neededLevelChange: boolean;
  distanceToNextLevel: number;
  orientationType: string;
  stepIdxDestination: number;
  indicationType: string;
  floorIdentifier?: number;
  nextFloorIdentifier?: number;
  metaData: string[];
  metaDataFence: string;
  nameFence: string;
}

export interface RouteSegment {
  points: RouteNode[];
  floorIdentifier: string;
}

export interface WhereAmIIndication {
  floorId?: number;
  distanceToPoi?: number;
  closestPoi?: number;
  humanReadableMessage?: string;
  orientationToPoi: number;
  fenceDescriptionMessage: string;
}

export type WhereAmIOptions = {
  simplified?: boolean;
};
